// views/Flowchart/FlowchartStudio.js
import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import "./FlowchartStudio.css";
import ModuleSidebar from "../../components/ModuleSidebar/ModuleSidebar";
import Flowchart from "./views/Flowchart/Flowchart";
import Services from "../../services/Services";
import { useLanguage } from "../../LanguageProvider";

const uid = (p = "flow") =>
  `${p}-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;

export default function FlowchartStudio() {
  const { getTranslation } = useLanguage();

  const token = localStorage.getItem("token");

  // KLASÖRSÜZ DÜZEN: sadece flow listesi ve seçili flow
  const [flows, setFlows] = useState([]);
  const [selectedFlowId, setSelectedFlowId] = useState("");

  const [isCreating, setIsCreating] = useState(false);
  const saveTimeoutRef = useRef(null);
  const lastScheduledReasonRef = useRef("");

  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [lastSavedPayload, setLastSavedPayload] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // --- SUNUCUDAN LİSTE ---
  useEffect(() => {
    let cancelled = false;

    const fetchFlows = async () => {
      if (!token) {
        setFlows([]);
        return;
      }
      try {
        const res = await Services.GetFlowcharts(token);
        const list = Array.isArray(res?.data?.result) ? res.data.result : [];

        // API -> UI mapping (flat)
        const mapped = list.map((it) => {
          const element = it?.flowchart?.element;
          const nodes = Array.isArray(element?.nodes) ? element.nodes : [];
          const edges = Array.isArray(element?.edges) ? element.edges : [];

          const createdMs = Number(it?.createdDateTime) * 1000 || Date.now();
          const updatedMs =
            Number(it?.last_update_date_time) * 1000 || createdMs;

          return {
            id: it?.flowchartID || it?.id || uid("flow"),
            title: it?.title || "Adsız",
            snapshot: { nodes, edges },
            createdAt: new Date(createdMs).toISOString(),
            updatedAt: new Date(updatedMs).toISOString(),
            unsaved: false,
          };
        });

        if (!cancelled) {
          setFlows(mapped);
          setSelectedFlowId((prev) => (prev ? prev : mapped[0]?.id || ""));
        }
      } catch (e) {
        console.error("GetFlowcharts error:", e);
        if (!cancelled) setFlows([]);
      }
    };

    fetchFlows();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // seçili yoksa akıllı seçim
  useEffect(() => {
    if (!selectedFlowId && flows.length > 0) {
      setSelectedFlowId(flows[0].id);
    }
  }, [flows, selectedFlowId]);

  const selectedFlow = useMemo(
    () => flows.find((f) => f.id === selectedFlowId),
    [flows, selectedFlowId]
  );

  const buildFlowPayload = useCallback((flow, reason = "unknown") => {
    return {
      id: flow.id, // flowchartID
      title: flow.title || "Adsız",
      snapshot: {
        nodes: Array.isArray(flow.snapshot?.nodes) ? flow.snapshot.nodes : [],
        edges: Array.isArray(flow.snapshot?.edges) ? flow.snapshot.edges : [],
      },
      createdAt: flow.createdAt,
      updatedAt: new Date().toISOString(),
      reason,
    };
  }, []);

  // === KAYDET: UpdateFlowchart ===
  const persistFlowSnapshot = useCallback(
    async (flow, reason = "unknown") => {
      if (!flow) return;
      const payload = buildFlowPayload(flow, reason);

      setIsSaving(true);
      try {
        if (token && payload.id) {
          await Services.UpdateFlowchart(
            token,
            payload.id,
            payload.title,
            payload.snapshot
          );
        }
      } catch (e) {
        console.error("UpdateFlowchart error:", e);
      } finally {
        setIsSaving(false);
      }

      setFlows((prev) =>
        prev.map((f) =>
          f.id === flow.id
            ? { ...f, unsaved: false, updatedAt: payload.updatedAt }
            : f
        )
      );
      setLastSavedAt(payload.updatedAt);
      setLastSavedPayload(payload);
      // eslint-disable-next-line no-console
    },
    [buildFlowPayload, token]
  );

  const scheduleSave = useCallback(
    (flowId, reason = "unknown") => {
      if (!flowId) return;
      lastScheduledReasonRef.current = reason;

      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        setFlows((prev) => {
          const flowToSave = prev.find((f) => f.id === flowId);
          if (!flowToSave) return prev;
          persistFlowSnapshot(flowToSave, lastScheduledReasonRef.current);
          return prev;
        });
      }, 1200);
    },
    [persistFlowSnapshot]
  );

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  // === FLOW OLUŞTUR ===
  const addNewFlow = useCallback(async () => {
    setIsCreating(true);
    try {
      const res = await Services.CreateFlowchart(token);
      const serverId =
        res?.data?.result?.flowchartID || res?.data?.result?.id || null;

      const id = serverId || uid("flow");
      const newFlow = {
        id,
        title: getTranslation("newDiagram"),
        snapshot: { nodes: [], edges: [] },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        unsaved: true,
      };

      setFlows((prev) => [newFlow, ...prev]);
      setSelectedFlowId(id);
      scheduleSave(id, "create");
    } catch (err) {
      console.error("CreateFlowchart error:", err);
      const localId = uid("flow");
      const newFlow = {
        id: localId,
        title: getTranslation("newDiagram"),
        snapshot: { nodes: [], edges: [] },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        unsaved: true,
      };
      setFlows((prev) => [newFlow, ...prev]);
      setSelectedFlowId(localId);
      scheduleSave(localId, "create-fallback");
    } finally {
      setIsCreating(false);
    }
  }, [scheduleSave, token]);

  const handleRenameFlow = useCallback(
    (flowId, newTitle) => {
      setFlows((prev) =>
        prev.map((f) =>
          f.id === flowId ? { ...f, title: newTitle || "Adsız", unsaved: true } : f
        )
      );
      scheduleSave(flowId, "rename");
    },
    [scheduleSave]
  );

  const handleDeleteFlow = useCallback(
    async (flowId) => {
      if (!flowId) return;

      // Seçili flow için bekleyen autosave varsa iptal
      if (saveTimeoutRef.current && selectedFlowId === flowId) {
        clearTimeout(saveTimeoutRef.current);
      }

      setIsDeleting(true);
      try {
        const isServerId =
          typeof flowId === "string" ? !flowId.startsWith("flow-") : true;

        if (token && isServerId) {
          // Sunucudan da kaldır
          let res = await Services.RemoveFlowchart(token, flowId);
        }

        // Lokal state'ten çıkar ve seçim güncelle
        setFlows((prev) => {
          const rest = prev.filter((f) => f.id !== flowId);
          // Eğer silinen seçiliyse, bir sonrakine (veya ilkine) geç
          setSelectedFlowId((prevSel) => {
            if (prevSel === flowId) {
              return rest[0]?.id || "";
            }
            return prevSel;
          });
          return rest;
        });
      } catch (e) {
        console.error("RemoveFlowchart error:", e);
        // İstersen burada toaster ile kullanıcıya hata gösterebilirsin
        // Toaster.error(getTranslation("deleteFailed") || "Silme işlemi başarısız.");
      } finally {
        setIsDeleting(false);
      }
    },
    [token, selectedFlowId]
  );

  const handleSubmenuClick = useCallback(
    (submenuItem) => {
      const maybeFlow = flows.find((f) => f.id === submenuItem.id);
      if (maybeFlow) setSelectedFlowId(maybeFlow.id);
    },
    [flows]
  );

  const handleFlowChange = useCallback(
    (nodes, edges) => {
      if (!selectedFlowId) return;

      setFlows((prev) =>
        prev.map((f) =>
          f.id === selectedFlowId
            ? { ...f, snapshot: { nodes, edges }, unsaved: true }
            : f
        )
      );

      scheduleSave(selectedFlowId, "content-change");
    },
    [selectedFlowId, scheduleSave]
  );

  const handleManualSave = useCallback(() => {
    if (!selectedFlowId) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    const toSave = flows.find((f) => f.id === selectedFlowId);
    if (!toSave) return;
    persistFlowSnapshot(toSave, "manual");
  }, [flows, selectedFlowId, persistFlowSnapshot]);

  const showUnsaved = !!selectedFlow?.unsaved;

  // KLASÖRSÜZ MENÜ: doğrudan flow listesini submenu olarak ver
  const sidebarSubmenu = useMemo(
    () =>
      flows.map((f) => ({
        id: f.id,
        title: f.title || "Adsız",
        unsaved: !!f.unsaved,
      })),
    [flows]
  );

  const menus = useMemo(
    () => [
      {
        sections: [
          {
            id: "flows-section",
            title: "Flow",
            icon: null,
            showEntityButton: true,
            submenu: sidebarSubmenu, // flat
          },
        ],
      },
    ],
    [sidebarSubmenu]
  );

  return (
    <div className="flowcharts-container">
      <ModuleSidebar
        title={"Flowchart"}
        menus={menus}
        selectedSubmenu={selectedFlowId}
        onSubmenuClick={handleSubmenuClick}
        addEntitiy={() => addNewFlow()}
        hideBranch={true}
        contextMenuRenameOnClick={(_, newTitle) =>
          selectedFlowId && handleRenameFlow(selectedFlowId, newTitle)
        }
        contextMenuDeleteOnClick={async (_) =>
          selectedFlowId && (await handleDeleteFlow(selectedFlowId))
        }
        deleteMessage={"Seçili flowchart silinecek. Bu işlem geri alınamaz."}
        checkPermission={() => true}
        onRightClick={() => { }}
        renameModalTitle={"Yeniden Adlandır"}
        showSearch={false}
      />

      <div className="flowcharts-content">
        <div className="flowcharts-inner-content">
          {selectedFlow ? (
            <>
              <div className="flow-topbar">
                <input
                  value={selectedFlow.title}
                  onChange={(e) =>
                    handleRenameFlow(selectedFlow.id, e.target.value)
                  }
                  className="editable-title-input"
                />

                <div className="topbar-right">
                  {isCreating && (
                    <div className="saved-badge">
                      {getTranslation("loading")}
                    </div>
                  )}

                  {isSaving && (
                    <div className="saving-indicator">
                      <span className="saving-spinner" />
                      <span className="saving-text">
                        {getTranslation("saving")}
                      </span>
                    </div>
                  )}

                  {isDeleting && (
                    <div className="saving-indicator">
                      <span className="saving-spinner" />
                      <span className="saving-text">
                        {getTranslation("deleting") || "Siliniyor..."}
                      </span>
                    </div>
                  )}

                  <button
                    className="shape-btn primary"
                    onClick={handleManualSave}
                    disabled={isSaving || !showUnsaved}
                  >
                    {getTranslation("save")}
                  </button>

                  <button
                    className="shape-btn"
                    onClick={() => handleDeleteFlow(selectedFlow.id)}
                    title="Sil"
                    disabled={isDeleting || isSaving}
                  >
                    {getTranslation("delete")}
                  </button>
                </div>
              </div>

              <Flowchart
                flowId={selectedFlow.id}
                snapshot={selectedFlow.snapshot}
                onChange={handleFlowChange}
              />
            </>
          ) : (
            <div style={{ padding: 16, opacity: 0.8 }}>
              Henüz diyagram yok. Soldaki başlıktaki + ikonuna basarak “Yeni
              Flowchart” ekleyin.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
